import React from 'react';
import { DEFAULT_SIGN_ABBREVS } from './chartConstants';

// North Indian diamond chart: square with inscribed diamond creating 12 house regions
// House 1 (ASC) is always top-center. Signs rotate based on ascendant.
// Houses numbered counter-clockwise from top.

const SIZE = 400;
const MID = SIZE / 2;

// House regions defined as polygon points (12 houses)
// Each house is a triangle or quadrilateral region
const HOUSE_PATHS = [
  // House 1 (top center) — diamond top
  `${MID},0 ${SIZE},0 ${MID},${MID}`,
  // House 2 (top-right corner) — small triangle
  `${SIZE},0 ${SIZE},${MID} ${MID},${MID}`,
  // House 3 (right-top)
  `${SIZE},0 ${SIZE},${MID} ${MID},${MID}`,
  // House 4 (right center) — diamond right
  `${SIZE},0 ${SIZE},${SIZE} ${MID},${MID}`,
  // House 5 (right-bottom)
  `${SIZE},${SIZE} ${SIZE},${MID} ${MID},${MID}`,
  // House 6 (bottom-right corner)
  `${SIZE},${SIZE} ${MID},${SIZE} ${MID},${MID}`,
  // House 7 (bottom center) — diamond bottom
  `${MID},${SIZE} 0,${SIZE} ${MID},${MID}`,
  // House 8 (bottom-left corner)
  `0,${SIZE} 0,${MID} ${MID},${MID}`,
  // House 9 (left-bottom)
  `0,${SIZE} 0,${MID} ${MID},${MID}`,
  // House 10 (left center) — diamond left
  `0,${SIZE} 0,0 ${MID},${MID}`,
  // House 11 (left-top)
  `0,0 0,${MID} ${MID},${MID}`,
  // House 12 (top-left corner)
  `0,0 ${MID},0 ${MID},${MID}`,
];

// Correct house regions as polygon paths for the standard North Indian layout:
// Outer square divided by diagonals and midpoint lines into 12 regions
const Q = SIZE / 4; // quarter
const H = SIZE / 2; // half
const T = (3 * SIZE) / 4; // three-quarter

// House text placement centroids (x, y) for planet labels
const HOUSE_CENTROIDS = [
  { x: H, y: Q * 0.8 },           // House 1: top center
  { x: T + Q / 2, y: Q * 0.8 },   // House 2: top-right
  { x: SIZE - Q * 0.55, y: H - Q * 0.3 }, // House 3: right-upper
  { x: SIZE - Q * 0.55, y: H },    // House 4: right center
  { x: SIZE - Q * 0.55, y: H + Q * 0.3 }, // House 5: right-lower
  { x: T + Q / 2, y: SIZE - Q * 0.8 },    // House 6: bottom-right
  { x: H, y: SIZE - Q * 0.8 },     // House 7: bottom center
  { x: Q / 2, y: SIZE - Q * 0.8 }, // House 8: bottom-left
  { x: Q * 0.55, y: H + Q * 0.3 }, // House 9: left-lower
  { x: Q * 0.55, y: H },           // House 10: left center
  { x: Q * 0.55, y: H - Q * 0.3 }, // House 11: left-upper
  { x: Q / 2, y: Q * 0.8 },        // House 12: top-left
];

const NorthIndianChart = ({ chartData, title = 'Kundli', loc = null }) => {
  if (!chartData) return null;

  const SIGN_ABBREVS = (loc && loc.signAbbrevs) || DEFAULT_SIGN_ABBREVS;
  const ascLabel = (loc && loc.ascAbbrev) || 'Asc';
  const { ascendant, planets } = chartData;
  const ascSignIdx = ascendant.sign_index;

  // In North Indian chart, House 1 = ascendant sign, House 2 = next sign, etc.
  // Map: house number → sign index
  const houseToSign = [];
  for (let i = 0; i < 12; i++) {
    houseToSign[i] = (ascSignIdx + i) % 12;
  }

  // Reverse map: sign index → house number
  const signToHouse = [];
  for (let i = 0; i < 12; i++) {
    signToHouse[houseToSign[i]] = i;
  }

  // Group planets by house
  const planetsByHouse = {};
  for (let i = 0; i < 12; i++) planetsByHouse[i] = [];

  // Place ascendant in house 0
  planetsByHouse[0].push({
    label: ascLabel,
    isAsc: true,
  });

  // Place planets
  planets.forEach(p => {
    const house = signToHouse[p.sign_index];
    if (house !== undefined) {
      planetsByHouse[house].push({
        label: `${p.abbrev || p.planet.substring(0, 2)}${p.is_retrograde ? '(R)' : ''}`,
        isAsc: false,
      });
    }
  });

  return (
    <div style={{ textAlign: 'center' }}>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width="100%"
        style={{ maxWidth: 420, border: '2px solid #333', background: '#fffef5' }}
      >
        {/* Outer square */}
        <rect x="0" y="0" width={SIZE} height={SIZE} fill="none" stroke="#333" strokeWidth="2" />

        {/* Diamond (diagonals) */}
        <line x1={0} y1={0} x2={SIZE} y2={SIZE} stroke="#333" strokeWidth="1" />
        <line x1={SIZE} y1={0} x2={0} y2={SIZE} stroke="#333" strokeWidth="1" />

        {/* Inner diamond */}
        <polygon
          points={`${H},0 ${SIZE},${H} ${H},${SIZE} 0,${H}`}
          fill="none"
          stroke="#333"
          strokeWidth="1"
        />

        {/* Center label */}
        <text x={H} y={H - 6} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">
          {title}
        </text>
        <text x={H} y={H + 10} textAnchor="middle" fontSize="10" fill="#888">
          {SIGN_ABBREVS[ascSignIdx]} Lagna
        </text>

        {/* Render house contents */}
        {Array.from({ length: 12 }).map((_, houseIdx) => {
          const signIdx = houseToSign[houseIdx];
          const centroid = HOUSE_CENTROIDS[houseIdx];
          const housePlanets = planetsByHouse[houseIdx] || [];

          // Sign label (small, at centroid top)
          const signLabelY = centroid.y - (housePlanets.length > 0 ? 12 : 0);

          return (
            <g key={houseIdx}>
              {/* Sign abbreviation */}
              <text
                x={centroid.x}
                y={signLabelY}
                textAnchor="middle"
                fontSize="11"
                fill="#888"
                fontWeight="500"
              >
                {SIGN_ABBREVS[signIdx]}
              </text>

              {/* Planets stacked vertically */}
              {housePlanets.map((p, i) => {
                const fontSize = housePlanets.length > 3 ? 10.5 : 12;
                const lineHeight = housePlanets.length > 3 ? 11 : 13;
                const startY = signLabelY + 12;
                const py = startY + i * lineHeight;

                return (
                  <text
                    key={i}
                    x={centroid.x}
                    y={py}
                    textAnchor="middle"
                    fontSize={fontSize}
                    fontWeight="600"
                    fill={p.isAsc ? '#c62828' : '#1a237e'}
                  >
                    {p.label}
                  </text>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default NorthIndianChart;
